import type {DetalleDore, MovimientosDore} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useMovimientosDore} from '../api/dore';
import {tr, trx, txt, type Texto} from '../i18n/idioma';

// Edicion de cobros y pagos, ensayos y liquidaciones de una exportacion de
// dore (decision 0094). Cada renglon es una tarjeta con sus campos; al
// guardar, la lista reemplaza la de la exportacion.
type Tipo = 'texto' | 'numero' | 'monto' | 'fecha' | 'porcentaje' | 'si-no' | 'casilla' | 'lista';
type Columna = {clave: string; etiqueta: Texto; tipo: Tipo; opciones?: Array<[string, Texto]>};
type Renglon = Record<string, string> & {id: string};
export type SeccionDore = 'cobros' | 'ensayos' | 'liquidaciones';

const TIPOS_DE_COBRO: Array<[string, Texto]> = [
  ['anticipo', txt('↓ Primer pago del comprador', '↓ First payment from the buyer')],
  ['saldo', txt('↓ Saldo del comprador', '↓ Balance from the buyer')],
  ['pago_proveedor', txt('↑ Pago al proveedor', '↑ Payment to the supplier')],
];
const RESULTADOS: Array<[string, Texto]> = [
  ['', txt('Sin resultado', 'No result')],
  ['aprobado', txt('Aprobado', 'Approved')],
  ['reanalisis', txt('Reanálisis', 'Re-assay')],
];
const LADOS: Array<[string, Texto]> = [
  ['comprador', txt('Comprador final con la intermediaria', 'Final buyer with the intermediary')],
  ['proveedor', txt('Intermediaria con el proveedor', 'Intermediary with the supplier')],
];

const COLUMNAS: Record<SeccionDore, Columna[]> = {
  cobros: [
    {clave: 'tipo', etiqueta: txt('Concepto', 'Concept'), tipo: 'lista', opciones: TIPOS_DE_COBRO},
    {clave: 'montoUsd', etiqueta: txt('USD', 'USD'), tipo: 'numero'},
    {clave: 'fecha', etiqueta: txt('Fecha', 'Date'), tipo: 'fecha'},
    {clave: 'motivoDeDemora', etiqueta: txt('Motivo de la demora', 'Delay reason'), tipo: 'texto'},
    {clave: 'comentarios', etiqueta: txt('Nota', 'Note'), tipo: 'texto'},
  ],
  ensayos: [
    {clave: 'resultado', etiqueta: txt('Resultado', 'Result'), tipo: 'lista', opciones: RESULTADOS},
    {clave: 'informeEl', etiqueta: txt('Informe', 'Report'), tipo: 'fecha'},
    {clave: 'oroOz', etiqueta: txt('Oro (oz)', 'Gold (oz)'), tipo: 'numero'},
    {clave: 'valorOroUsd', etiqueta: txt('Valor del oro (USD)', 'Gold value (USD)'), tipo: 'numero'},
    {clave: 'plataOz', etiqueta: txt('Plata (oz)', 'Silver (oz)'), tipo: 'numero'},
    {clave: 'valorPlataUsd', etiqueta: txt('Valor de la plata (USD)', 'Silver value (USD)'), tipo: 'numero'},
    {clave: 'cargosUsd', etiqueta: txt('Cargos (USD)', 'Charges (USD)'), tipo: 'numero'},
    {clave: 'reanalisis', etiqueta: txt('Reanálisis', 'Re-assay'), tipo: 'si-no'},
    {clave: 'informeFinalEl', etiqueta: txt('Informe final', 'Final report'), tipo: 'fecha'},
    {clave: 'informeProveedorEl', etiqueta: txt('Informe del proveedor', 'Supplier report'), tipo: 'fecha'},
    {clave: 'respuestaProveedorEl', etiqueta: txt('Respuesta del proveedor', 'Supplier answer'), tipo: 'fecha'},
    {clave: 'respuestaRefineriaEl', etiqueta: txt('Respuesta de la refinería', 'Refinery answer'), tipo: 'fecha'},
    {clave: 'reanalisisFinalEl', etiqueta: txt('Fin del reanálisis', 'Re-assay end'), tipo: 'fecha'},
    {clave: 'ensayoProveedor', etiqueta: txt('Ensayo del proveedor', 'Supplier assay'), tipo: 'texto'},
  ],
  liquidaciones: [
    {clave: 'lado', etiqueta: txt('Liquidación', 'Settlement'), tipo: 'lista', opciones: LADOS},
    {clave: 'valorUsd', etiqueta: txt('Valor (USD)', 'Value (USD)'), tipo: 'monto'},
    {clave: 'pagableOro', etiqueta: txt('Oro pagable (%)', 'Gold payability (%)'), tipo: 'porcentaje'},
    {clave: 'liquidadaEl', etiqueta: txt('Liquidada el', 'Settled on'), tipo: 'fecha'},
    {clave: 'aceptadaEl', etiqueta: txt('Aceptada el', 'Accepted on'), tipo: 'fecha'},
    {clave: 'pagoFinalEl', etiqueta: txt('Pago final', 'Final payment'), tipo: 'fecha'},
    {clave: 'referenciaContable', etiqueta: txt('Número contable', 'Accounting number'), tipo: 'texto'},
    {clave: 'pendiente', etiqueta: txt('Pendiente', 'Pending'), tipo: 'casilla'},
  ],
};

const TITULOS: Record<SeccionDore, Texto> = {
  cobros: txt('Editar cobros y pagos', 'Edit collections and payments'),
  ensayos: txt('Editar ensayo', 'Edit assay'),
  liquidaciones: txt('Editar liquidaciones', 'Edit settlements'),
};

const comoTexto = (v: unknown) => (v === null || v === undefined ? '' : String(v));

function renglonesDe(d: DetalleDore, seccion: SeccionDore): Renglon[] {
  const lista: Array<Record<string, unknown>> = d[seccion];
  return lista.map(r => {
    const renglon: Renglon = {id: String(r.id)};
    for (const c of COLUMNAS[seccion]) {
      const v = r[c.clave];
      if (c.tipo === 'porcentaje') renglon[c.clave] = v === null || v === undefined ? '' : String(Number(v) * 100);
      else if (c.tipo === 'si-no') renglon[c.clave] = v === true ? 'si' : v === false ? 'no' : '';
      else if (c.tipo === 'casilla') renglon[c.clave] = v ? 'si' : '';
      else renglon[c.clave] = comoTexto(v);
    }
    return renglon;
  });
}

function nuevoRenglon(seccion: SeccionDore, existentes: Renglon[]): Renglon {
  const r: Renglon = {id: ''};
  for (const c of COLUMNAS[seccion]) r[c.clave] = '';
  if (seccion === 'cobros') r.tipo = existentes.some(x => x.tipo === 'anticipo') ? 'saldo' : 'anticipo';
  if (seccion === 'liquidaciones') r.lado = existentes.some(x => x.lado === 'comprador') ? 'proveedor' : 'comprador';
  return r;
}

// Renglones del formulario → la lista para la API.
function aLista(seccion: SeccionDore, renglones: Renglon[]): {lista: Array<Record<string, unknown>>} | {error: string} {
  const lista: Array<Record<string, unknown>> = [];
  for (const [i, r] of renglones.entries()) {
    const fila: Record<string, unknown> = {id: r.id ? Number(r.id) : null};
    for (const c of COLUMNAS[seccion]) {
      const texto = (r[c.clave] ?? '').trim();
      if (c.tipo === 'si-no') fila[c.clave] = texto === 'si' ? true : texto === 'no' ? false : null;
      else if (c.tipo === 'casilla') fila[c.clave] = texto === 'si';
      else if (c.tipo === 'numero' || c.tipo === 'monto' || c.tipo === 'porcentaje') {
        if (!texto) {
          fila[c.clave] = null;
          continue;
        }
        const n = Number(texto.replace(/\s/g, '').replace(',', '.'));
        const fuera = !Number.isFinite(n) || (c.tipo !== 'monto' && n < 0) || (c.tipo === 'porcentaje' && n > 100);
        if (fuera)
          return {
            error: tr(
              `Renglón ${i + 1}, ${trx(c.etiqueta)}: escribe un número válido.`,
              `Row ${i + 1}, ${trx(c.etiqueta)}: enter a valid number.`,
            ),
          };
        fila[c.clave] = c.tipo === 'porcentaje' ? n / 100 : n;
      } else if (c.tipo === 'lista') fila[c.clave] = texto || null;
      else fila[c.clave] = texto || null;
    }
    lista.push(fila);
  }
  return {lista};
}

export function EdicionDeMovimientosDore({
  d,
  seccion,
  onCerrar,
}: {
  d: DetalleDore;
  seccion: SeccionDore;
  onCerrar: () => void;
}) {
  const guardarMovimientos = useMovimientosDore(d.codigo);
  const [renglones, setRenglones] = useState<Renglon[]>(() => {
    const iniciales = renglonesDe(d, seccion);
    return iniciales.length ? iniciales : [nuevoRenglon(seccion, [])];
  });
  const [error, setError] = useState<string | null>(null);
  const cambiar = (i: number, clave: string, valor: string) =>
    setRenglones(rs => rs.map((r, j) => (j === i ? {...r, [clave]: valor} : r)));

  const guardar = async () => {
    setError(null);
    const resultado = aLista(seccion, renglones);
    if ('error' in resultado) return setError(resultado.error);
    try {
      await guardarMovimientos.mutateAsync({
        version: d.version,
        [seccion]: resultado.lista,
      } as MovimientosDore);
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  return (
    <form
      aria-label={trx(TITULOS[seccion])}
      className="space-y-3"
      onSubmit={e => {
        e.preventDefault();
        void guardar();
      }}
    >
      {renglones.map((r, i) => (
        <div key={r.id || `nuevo-${i}`} className="rounded-md border border-linea p-3">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {COLUMNAS[seccion].map(c => (
              <label key={c.clave} className="block text-xs text-apagado">
                {trx(c.etiqueta)}
                {c.tipo === 'lista' ? (
                  <select
                    aria-label={trx(c.etiqueta)}
                    value={r[c.clave]}
                    onChange={e => cambiar(i, c.clave, e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
                  >
                    {c.opciones!.map(([valor, etiqueta]) => (
                      <option key={valor} value={valor}>
                        {trx(etiqueta)}
                      </option>
                    ))}
                  </select>
                ) : c.tipo === 'si-no' ? (
                  <select
                    aria-label={trx(c.etiqueta)}
                    value={r[c.clave]}
                    onChange={e => cambiar(i, c.clave, e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
                  >
                    <option value="">—</option>
                    <option value="si">{tr('Sí', 'Yes')}</option>
                    <option value="no">{tr('No', 'No')}</option>
                  </select>
                ) : c.tipo === 'casilla' ? (
                  <input
                    aria-label={trx(c.etiqueta)}
                    type="checkbox"
                    checked={r[c.clave] === 'si'}
                    onChange={e => cambiar(i, c.clave, e.target.checked ? 'si' : '')}
                    className="ml-2 align-middle"
                  />
                ) : (
                  <input
                    aria-label={trx(c.etiqueta)}
                    type={c.tipo === 'fecha' ? 'date' : 'text'}
                    inputMode={c.tipo === 'texto' || c.tipo === 'fecha' ? undefined : 'decimal'}
                    value={r[c.clave]}
                    onChange={e => cambiar(i, c.clave, e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta tabular-nums"
                  />
                )}
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setRenglones(rs => rs.filter((_, j) => j !== i))}
            className="mt-2 text-xs text-alerta hover:underline"
          >
            {tr('Quitar este renglón', 'Remove this row')}
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRenglones(rs => [...rs, nuevoRenglon(seccion, rs)])}
        className="rounded-md border border-linea bg-white px-3 py-1 text-sm hover:bg-papel"
      >
        {tr('＋ Agregar renglón', '＋ Add row')}
      </button>
      {error && (
        <p className="text-sm text-alerta" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={guardarMovimientos.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Guardar cambios', 'Save changes')}
        </button>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
        >
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}
